<template>
    <LazyHydration :hydrate="hydrateOnIdle()">
        <HeadMetaTwoPage
            :title="headTitle"
            :description="headDescription"
        />
    </LazyHydration>
    <div :class="$style.page">
        <div :class="$style.content">
            <header :class="$style.header">
                <Link
                    :to="{ name: ROUTE_NAMES.WHITE_LABEL_2PAGE, query: route.query }"
                    data-interface-target="logo-link"
                    :class="$style.logo_link"
                >
                    <TwoPageBrand />
                </Link>
            </header>
            <main
                v-if="section"
                :class="$style.main"
            >
                <Link
                    :to="{ name: ROUTE_NAMES.WHITE_LABEL_2PAGE, query: route.query }"
                    color="white-60"
                    :class="$style.back_link"
                >
                    {{ $tr('game.main_title') }}
                </Link>
                <section :class="$style.section">
                    <ShelfTitle
                        :title="section.title"
                        :categoryId="section.categoryId"
                    />
                    <div :class="$style.grid">
                        <GameCard
                            v-for="game in section.allGames"
                            :key="game.hru"
                            :game="game"
                            :to="buildTwoPageLaunchRoute(game.hru, route.query)"
                            type="rectangle"
                            appearance="medium_horizontal"
                            responsive
                        />
                    </div>
                </section>
            </main>
            <TwoPageFooter />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, hydrateOnIdle, onMounted, onServerPrefetch } from 'vue'
import { useRoute } from 'vue-router'

import GameCard from '@/components/GameCard/GameCard.vue'
import ShelfTitle from '@/components/GamesShelf/ShelfTitle.vue'
import LazyHydration from '@/components/LazyHydration.vue'
import Link from '@/components/Link.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'

import HeadMetaTwoPage from './HeadMetaTwoPage.vue'
import { buildTwoPageLaunchRoute, getTwoPageCategoryHruBySlug } from './two-page-data'
import TwoPageBrand from './TwoPageBrand.vue'
import TwoPageFooter from './TwoPageFooter.vue'
import { useTwoPageCatalogData } from './use-two-page-catalog-data'

const route = useRoute()
const appStore = useAppStore()
const { allSections, preloadCatalogData } = useTwoPageCatalogData()

const categoryHru = computed(() => getTwoPageCategoryHruBySlug(route.params.categorySlug as string) || '')
const section = computed(() => allSections.value.find((item) => item.mainCategory === categoryHru.value))

const headTitle = computed(() => section.value?.title || 'Games')
const headDescription = computed(() =>
    section.value ? `Browse all ${section.value.title.toLowerCase()} available in the catalog.` : 'Game catalog',
)

function processResponse() {
    if (!categoryHru.value || !section.value) {
        appStore.setPageStatus(404)
    }
}

onServerPrefetch(async () => {
    await preloadCatalogData()
    processResponse()
})

onMounted(async () => {
    await preloadCatalogData()
    processResponse()
})
</script>

<style module>
.page {
    min-height: 100svh;
    background:
        radial-gradient(circle at top, rgba(87, 132, 255, 0.18), transparent 34%),
        linear-gradient(180deg, #090d17 0%, #05070d 100%);
    padding: var(--pg-safe-area-top) var(--pg-safe-area-right) var(--pg-safe-area-bottom) var(--pg-safe-area-left);
}

.content {
    width: min(1200px, 100%);
    margin: 0 auto;
    padding: 8px 8px 28px;
}

.header {
    padding: 0 0 12px;
}

.logo_link {
    box-sizing: border-box;
    width: fit-content;
    min-height: 48px;
    border-radius: 24px;
    background: var(--purple-400);
    display: inline-flex;
    align-items: center;
    padding: 0 clamp(12px, 3vw, 18px);
    box-shadow: inset 0 0 0 1px var(--white-25);
}

.main {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.back_link {
    width: fit-content;
}

.section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 8px;
    --cards-gap: 8;
}

@media (--mobile-landscape) {
    .grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (--tablet-plus) {
    .content {
        padding-inline: 0;
    }

    .header {
        padding-bottom: 18px;
    }

    .logo_link {
        min-height: 56px;
        padding-inline: 20px;
        border-radius: 62px;
    }

    .grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 16px 12px;
        --cards-gap: 12;
    }
}
</style>
